import { Button, Stack } from "@mui/material";
import { parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { numberComparerDescBy } from "@entrilia/shared/utilities/arrayHelper";
import { api } from "../../../../api/client";
import useImpersonationBlockContext from "../../../../contexts/useImpersonationBlockContext";
import ChangeRequestStatus from "./ChangeRequestStatus";
import EditAccountSettingsForm from "./EditAccountSettingsForm";
import {
  getInitialState,
  loadDataAction,
  mapFormStateToUpdateRequest,
  resetAction,
  setErrorsAction,
  validateForm,
} from "./accountSettingsEditState";

interface Props {
  canUserEdit: boolean;
  investorId: string;
}

const updateInvestorDetails = withErrorHandling(api.investorDetails.updateInvestorDetails);

const EditAccountSettings = ({ canUserEdit, investorId }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();
  const [formState, setFormState] = useState(getInitialState);
  const [isSendingChangeRequest, setIsSendingChangeRequest] = useState(false);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);

  const getInvestorDetails = useCallback(() => api.investorDetails.getInvestorDetails(investorId), [investorId]);

  const getPendingChangeRequests = useCallback(
    () => api.changeRequests.getPendingChangeRequests(investorId, "InvestorDetailsChangeRequest"),
    [investorId]
  );

  const [investor, fetchInvestorError, { isFetching: isFetchingInvestor }] = useFetch(getInvestorDetails, {
    onSetData: (investor) => setFormState(loadDataAction(investor)),
  });

  const [
    pendingChangeRequestsInfo,
    fetchChangeRequestsError,
    { setData: setPendingChangeRequestsInfo, isFetching: isFetchingChangeRequests },
  ] = useFetch(getPendingChangeRequests);

  if (investor === undefined || isFetchingInvestor || isFetchingChangeRequests) {
    return <InlineLoader />;
  }

  const handleSendRequest = async () => {
    const errors = validateForm(formState);
    setFormState(setErrorsAction(errors));
    if (Object.values(errors).some((error) => !!error)) {
      return;
    }

    setIsSendingChangeRequest(true);

    const [changeRequestDetails, error] = await updateInvestorDetails(
      investorId,
      mapFormStateToUpdateRequest(formState.values, formState.attachments)
    );

    setIsSendingChangeRequest(false);

    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[AccountSettingsEditForm] handleSendRequest");
      sendNotificationError(error.message);
      return;
    }

    setPendingChangeRequestsInfo({ changeRequestDetails: [changeRequestDetails] });

    setFormState(resetAction());
  };

  const handleCancel = () => {
    setFormState(resetAction());
  };

  const handleChangeRequestCanceled = () => {
    setPendingChangeRequestsInfo({ changeRequestDetails: [] });
  };

  const latestPendingChangeRequest = pendingChangeRequestsInfo?.changeRequestDetails.sort(
    numberComparerDescBy((d) => parseISO(d.requestedAt).getDate())
  )[0];

  const allowFormEdit = canUserEdit && !isSendingChangeRequest && !latestPendingChangeRequest;

  return (
    <Stack spacing={3} sx={{ width: "100%", height: "100%" }}>
      <ChangeRequestStatus
        canUserEdit={canUserEdit}
        investorId={investorId}
        investorName={investor.name || ""}
        fetchError={fetchInvestorError || fetchChangeRequestsError}
        pendingChangeRequest={latestPendingChangeRequest}
        onChangeRequestCanceled={handleChangeRequestCanceled}
      />
      <Stack spacing={4} sx={{ width: "100%" }}>
        <EditAccountSettingsForm
          investorId={investorId}
          formState={formState}
          setFormState={setFormState}
          allowEdit={allowFormEdit}
          onAttachmentsBusyChange={setAttachmentsBusy}
        />

        {canUserEdit && (
          <Stack direction="row" spacing={1}>
            <Button
              loading={isSendingChangeRequest}
              variant="contained"
              disabled={!formState.isDirty || !allowFormEdit || attachmentsBusy}
              onClick={handleSendRequest}
            >
              Send Request
            </Button>
            <Button
              variant="text"
              color="secondary"
              disabled={!formState.isDirty || !allowFormEdit || attachmentsBusy}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default EditAccountSettings;
